import React, { FC } from 'react'
import { CircularProgress, Step, StepLabel, Stepper, Typography, useMediaQuery, useTheme } from '@mui/material'
import { ContractorTransfer, ContractorTransferStageEnum } from 'src/api'
import { getTransferStages, summarizeReadingsProgress, TransferStage } from 'src/components/columns/contractorTransferHelpers'

interface ContractorTransferStagesProps {
  transfer: ContractorTransfer
}

const getStageCaption = (stage: TransferStage, transfer: ContractorTransfer): string | null => {
  const counts = transfer.counts || {}

  switch (stage.stage) {
    case ContractorTransferStageEnum.Readings: {
      const progress = summarizeReadingsProgress(transfer.progress)
      if (!progress) return null
      return `${progress.rows.toLocaleString()} rows in ${progress.finishedTables} of ${progress.tables} tables`
    }
    case ContractorTransferStageEnum.Heal:
      return counts.healed !== undefined ? `${counts.healed.toLocaleString()} rows healed` : null
    case ContractorTransferStageEnum.Verify:
      return counts.verify_left !== undefined ? `${counts.verify_left.toLocaleString()} rows left` : null
    default:
      return null
  }
}

/** The four stages of a transfer job, with the progress the backend reports for each. */
export const ContractorTransferStages: FC<ContractorTransferStagesProps> = ({ transfer }) => {
  const theme = useTheme()
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true })
  const stages = getTransferStages(transfer)
  // -1 while nothing runs (queued, waiting between stages, finished); the done steps carry their own `completed`
  const activeStep = stages.findIndex((stage) => stage.state === 'active' || stage.state === 'failed')

  return (
    <Stepper activeStep={activeStep} orientation={isPhone ? 'vertical' : 'horizontal'} alternativeLabel={!isPhone}>
      {stages.map((stage) => {
        const caption = getStageCaption(stage, transfer)
        const isActive = stage.state === 'active'

        return (
          <Step key={stage.stage} completed={stage.state === 'done'}>
            <StepLabel
              error={stage.state === 'failed'}
              icon={isActive ? <CircularProgress size={22} /> : undefined}
              optional={
                caption || stage.state === 'skipped' ? (
                  <Typography variant="caption" color="text.secondary">
                    {stage.state === 'skipped' ? 'Not run' : caption}
                  </Typography>
                ) : undefined
              }
            >
              {stage.label}
            </StepLabel>
          </Step>
        )
      })}
    </Stepper>
  )
}
